import {
  AnalyticsTrackingEvent,
  BuyBoxDialog,
  ProductRewardSearchResult,
  RetailerIconGroup,
} from '@brandclub/common-ui';
import { styled } from '@mui/material';
import { useSplitTreatments } from '@splitsoftware/splitio-react';

import { useDomainConfig } from '@/api/cache/appConfigQueryOptions';
import { useTrackActions } from '../../hooks/useTracking';
import { getProductsToListForBuyBox } from './inpageAddToBagHelpers';

const MoreRetailersInfoContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  marginTop: '24px',
  width: '100%',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '12px',

  '.bc_more_retailers_info_text': {
    fontSize: '16px',
    fontWeight: 600,
    color: theme.vars.palette.text.primary,
    fontFamily: theme.typography.fontFamily,
  },
}));

export const MoreRetailersInfo = ({
  product,
  handleBuyBoxBuyClick,
  hideMatchedRetailOffers,
  checkoutRetailerIds,
}: {
  product?: ProductRewardSearchResult;
  handleBuyBoxBuyClick: (product: ProductRewardSearchResult) => void;
  hideMatchedRetailOffers?: boolean;
  checkoutRetailerIds?: number[];
}) => {
  const { data: domainConfig } = useDomainConfig();
  const [trackCheckoutBarAction] = useTrackActions();
  const productsToList = getProductsToListForBuyBox(product, checkoutRetailerIds);
  const retailerIds = productsToList.map((product) => product.retailerId);
  const primaryProduct = productsToList[0];
  const primaryRetailerId = primaryProduct?.retailerId;
  const primaryRetailerDisplayName = primaryProduct?.retailerDisplayName;

  const { treatments } = useSplitTreatments({
    names: ['brandclub_checkout_disabled_domain'],
    attributes: {
      domain: window.location.hostname,
      bcSplit: (window as any).bcSplit,
    },
  });

  const disableCheckoutByDomain = treatments?.brandclub_checkout_disabled_domain?.treatment === 'disable';

  const handleViewAllRetailers = () => {
    BuyBoxDialog.show({
      products: productsToList,
      checkoutAllowedOfferType: domainConfig?.checkoutAllowedOfferType,
      disabledCheckout: disableCheckoutByDomain,
      onClick: handleBuyBoxBuyClick,
      onProductPageClick: (product) => {
        trackCheckoutBarAction(AnalyticsTrackingEvent.WEB_APP_ORGANIC_OPEN_RETAILER_PRODUCT_PAGE, {
          stacklineSku: product.stacklineSku,
          retailerId: product.retailerId,
          retailerSku: product.retailerSku,
          retailPrice: product.retailPrice,
          organicType: 'openRetailerProductPage',
          source: 'brandclub_dtc_view_all_retail_offers',
        });
        window.open(product.retailerProductPageUrl, '_blank');
      },
    });
  };

  if (productsToList.length > 1) {
    if (hideMatchedRetailOffers) {
      return null;
    }

    return (
      <MoreRetailersInfoContainer
        sx={{ cursor: 'pointer' }}
        onClick={handleViewAllRetailers}
        className="bc-more-retailers-info-container"
      >
        <RetailerIconGroup
          retailerIds={retailerIds}
          iconSize={24}
        />
        <div className="bc_more_retailers_info_text view-all-retailers-text">View all retail offers</div>
      </MoreRetailersInfoContainer>
    );
  } else if (primaryRetailerId && primaryRetailerDisplayName) {
    return (
      <MoreRetailersInfoContainer className="bc-more-retailers-info-container">
        <RetailerIconGroup retailerIds={[primaryRetailerId]} iconSize={24} />
        <div className="bc_more_retailers_info_text expedited-shipping-text">
          Expedited shipping from {primaryRetailerDisplayName}
        </div>
      </MoreRetailersInfoContainer>
    );
  } else {
    return null;
  }
};
